import React from "react";
import { useLocation } from "react-router-dom";

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "GHS 0.00";
  const isNegative = Number(value) < 0;
  const num = Math.abs(Number(String(value).replace(/[^\d.]/g, "")));
  if (isNaN(num)) return value;
  const sign = isNegative ? "-" : "";
  return `GHS ${sign}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function say(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
}

function CorporateProductDetails({ productDetails }) {
  // Normalize incoming productDetails shape to a predictable object `pd` used by this UI.
  // Supports both the raw API shape (snake_case / PascalCase) and the mapped camelCase shape.
  const pd = React.useMemo(() => {
    if (!productDetails) return {};

    const statusRaw = productDetails.status_description || productDetails.Status || productDetails.status || "";

    return {
      memberId: productDetails.memberId || productDetails.userId || productDetails.MemberId || productDetails.member_id || "",
      schemeId: productDetails.schemeId || productDetails.companyId || productDetails.SchemeID || productDetails.scheme_id || "",
      policyNumber: productDetails.policyNumber || productDetails.policy_no || productDetails.number || "",
      memberNo: productDetails.memberNo || productDetails.member_no || productDetails.staffId || productDetails.staff_id || "",
      memberName: productDetails.memberName || productDetails.employee_name || productDetails.policyName || productDetails.name || productDetails.companyName || "",
      companyName: productDetails.companyName || productDetails.company_name || "",
      age: productDetails.age || "",
      gender: productDetails.gender || "",
      salary: productDetails.salary || 0,
      monthlySalary: productDetails.monthly_salary || productDetails.monthlySalary || 0,
      sumAssured: productDetails.sumAssured || productDetails.sum_assured || 0,
      premium: productDetails.premium || productDetails.premiumAmount || 0,
      commenceDate: productDetails.commenceDate || productDetails.commence_date || productDetails.startDate || "",
      dateJoined: productDetails.date_joined || productDetails.dateJoined || "",
      exitDate: productDetails.exit_date || productDetails.exitDate || "",
      mobile: productDetails.mobile || "",
      email: productDetails.email || "",
      jobTitle: productDetails.job_title || productDetails.jobTitle || "",
      occupation: productDetails.Occupation || productDetails.occupation || "",
      classCode: productDetails.classCode || productDetails.class_code || "",
      uwYear: productDetails.uw_year || productDetails.uwYear || "",
      dateOfBirth: productDetails.date_of_birth || productDetails.dateOfBirth || "",
      status: statusRaw,
      statusDescription: productDetails.statusDescription || productDetails.status_description || String(statusRaw || ""),
    };
  }, [productDetails]);

  if (!productDetails || (!pd.policyNumber && !pd.memberName)) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div style={{ textAlign: "center", maxWidth: 720 }}>
          <h3 style={{ marginBottom: 8 }}>No product selected</h3>
          <p style={{ color: "#666", marginBottom: 16 }}>Please open a product from the dashboard or products list to see the corporate account details.</p>
          <div>
            <a href="/dashboard" className="btn btn-white" style={{ marginRight: 8 }}>Back to Dashboard</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="life-details-page"
      style={{
        minHeight: "80vh",
        background: "#f5f6fa",
        padding: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 32,
      }}
    >
      <div
        className="product-details-wrapper"
        style={{
          width: "100%",
          maxWidth: 700,
          display: "flex",
          flexDirection: "column",
          gap: 24,
          alignItems: "center",
        }}
      >
        {/* Corporate Account Details Card */}
        <div
          className="product-details-card"
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
            padding: "32px 48px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            maxWidth: 700,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 800,
              fontSize: 20,
              color: "#000000ff",
              marginBottom: 12,
              width: "100%",
            }}
          >
            <span>{pd.memberName || pd.companyName}</span>
          </div>
          <div style={{ height: 18 }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* 1. Policy Number */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.policyNumber)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Policy Number</div>
            </div>
            {/* 2. Employee Name */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.memberName)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Employee Name</div>
            </div>
            {/* 3. Company Name */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.companyName)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Company Name</div>
            </div>
            {/* 4. Member Number */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.memberNo)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Member Number</div>
            </div>
            {/* 5. Member ID */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.memberId)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Member ID</div>
            </div>
            {/* 6. Scheme ID */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.schemeId)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Scheme ID</div>
            </div>
            {/* 7. Status */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.statusDescription)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Status</div>
            </div>
            {/* 8. Class Code */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.classCode)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Class Code</div>
            </div>
            {/* 9. Sum Assured */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{formatCurrency(pd.sumAssured)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Sum Assured</div>
            </div>
            {/* 10. Salary */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{formatCurrency(pd.salary)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Salary</div>
            </div>
            {/* 11. Premium */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{formatCurrency(pd.premium)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Premium</div>
            </div>
            {/* 12. Commence Date */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{formatDate(pd.commenceDate)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Commence Date</div>
            </div>
            {/* 13. Date of Birth */}
            {pd.dateOfBirth && (
              <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
                <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{formatDate(pd.dateOfBirth)}</div>
                <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Date of Birth</div>
              </div>
            )}
            {/* 14. Age */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.age)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Age</div>
            </div>
            {/* 15. Gender */}
            <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.gender)}</div>
              <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Gender</div>
            </div>
            {/* 16. Mobile */}
            {pd.mobile && (
              <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
                <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.mobile)}</div>
                <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Mobile</div>
              </div>
            )}
            {/* 17. Occupation */}
            {pd.occupation && (
              <div style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
                <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>{say(pd.occupation)}</div>
                <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>Occupation</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Default export: wrapper that reads from location state for dynamic navigation
export default function CorporateProductDetailsWrapper() {
  const location = useLocation();

  const { productDetails } = location.state || {};

  return <CorporateProductDetails productDetails={productDetails} />;
}

// Also export the component for direct use
export { CorporateProductDetails };